import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { ANALYSIS_API, type Analysis } from '@chess/api-analysis';
import { GAMES_API, type Games } from '@chess/api-games';
import { LOG_API, type Log } from '@chess/api-log';
import { STORE_API, type GameStore } from '@chess/api-model';
import { SCAN_API, ScanQueue } from '@chess/api-scan';
import { DashboardHeader } from './DashboardHeader.tsx';
import './dashboard-header.css';

/** What a dashboard screen passes to `shared.dashboardHeader`. */
export interface DashboardHeaderParams {
  /** The screen's name: "Home", "Openings", "Middlegame". */
  screen: string;
  /** A muted line under the bar, e.g. "188 games · 121 scanned". */
  caption?: string;
  /** The period on the title; `null` hides it (Settings). Default "Last 30 days". */
  period?: string | null;
  /** A slot of this screen's own actions, shown before the shared ones (Home: `home.headerActions`). */
  actions?: string;
}

/** The slot whose extensions render on every header's right: the importer, which also shows the scan's progress. */
export const HEADER_ACTIONS = 'shared.headerActions';

// The scan API (one queue for every engine scan, so the engine works through
// one list) and the automatic scan: once the store has loaded, and again on
// every change of it (an import adds games), every game of the last 30 days
// without a complete scan is queued, newest first, one at a time. There is no
// scan button; the importer's button shows the progress. Also the top bar of
// every screen on the rail, extension `shared.dashboardHeader`.
// See docs/architecture/core/plugin-shared-scan.md.
export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  void (async () => {
    const [store, analysis, log] = await Promise.all([
      app.getApi<GameStore>(STORE_API),
      app.getApi<Analysis>(ANALYSIS_API),
      app.getApi<Log>(LOG_API).then((l) => l.child(app.meta.name)),
    ]);
    const queue = new ScanQueue({
      // The store's games: the last 30 days, the window every scope is clamped to.
      list: async () => {
        await store.ready();
        return store.list({ order: 'newest' });
      },
      isComplete: async (id) => {
        const status = analysis.status(id);
        if (status?.running) return false;
        // The store's facts say it for the reader's games; others are read.
        const facts = store.game(id)?.facts;
        if (facts) return facts.scanned;
        return !!(await analysis.get(id))?.complete;
      },
      scan: (id) => analysis.scan(id),
      onComplete: (listener) =>
        analysis.subscribe((e) => {
          if (e.type === 'complete') listener(e.gameId);
        }),
      warn: (message, fields) => log.warn(message, fields as never),
    });
    app.registerApi(SCAN_API, queue);
    // A game removed from the library (the account wiped from the landing
    // page) is forgotten, so the same id imported again is scanned again.
    void app.getApi<Games>(GAMES_API).then((games) =>
      games.subscribe((e) => {
        if (e.type === 'removed') queue.forget(e.id);
      }),
    );

    // The automatic scan: on load, and whenever the store changes (games
    // imported or removed, a scan completed). A pass that finds nothing new
    // queues nothing; passes asked while one reads coalesce.
    await store.ready();
    const scanAll = () => void queue.scanAll().catch((error) => log.warn('automatic scan failed', { error: String(error) }));
    let revision = store.getState().revision;
    scanAll();
    store.subscribe((state) => {
      if (state.revision === revision) return;
      revision = state.revision;
      scanAll();
    });
  })();

  // The top bar of every screen on the rail: "<screen> · Last 30 days", then
  // on the right the screen's own actions (its `actions` slot, if any), then
  // whatever `shared.headerActions` holds (the importer, showing the import
  // and then the scan's progress).
  const Header: React.FC<{ params: DashboardHeaderParams }> = ({ params }) => (
    <DashboardHeader
      screen={params.screen}
      period={params.period}
      caption={params.caption}
      action={
        <>
          {params.actions && <app.Extension name={params.actions} />}
          <app.Extension name={HEADER_ACTIONS} />
        </>
      }
    />
  );
  app.registerExtension<DashboardHeaderParams>('shared.dashboardHeader', Header);
}
